import { Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { DataIcon } from '@/components/icons/DataIcon';
import { DiagramIcon } from '@/components/icons/DiagramIcon';
import { DocumentIcon, IconProps } from '@/components/icons/DocumentIcon';
import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { COLORS } from '@/styles/tokens/colors';

/**
 * The kinds of product objects that can be attached to a chat message as
 * context. Each type maps to a distinct icon in the context bar.
 */
export type ChatComposerContextItemType =
  | 'document'
  | 'waterfall'
  | 'presentationSlide'
  | 'entity';

export interface ChatComposerContextItem {
  type: ChatComposerContextItemType;
  /** Display name of the underlying object, e.g. the document or entity name */
  name: string;
}

export interface ChatComposerContextBarProps {
  item: ChatComposerContextItem;
  /** Called when the user dismisses the context via the close button */
  onRemove: () => void;
}

const ICON_BY_CONTEXT_TYPE: Record<
  ChatComposerContextItemType,
  ComponentType<IconProps>
> = {
  document: DocumentIcon,
  waterfall: DiagramIcon,
  presentationSlide: PresentationChart01Icon,
  entity: DataIcon,
};

/**
 * Header strip docked at the top of the chat composer showing the object the
 * next message will use as context (e.g. a document, waterfall, presentation
 * slide, or entity), with a close button to remove it.
 *
 * Purely presentational: render it conditionally (only when there is an
 * attached context) via ChatComposer's `contextBar` slot.
 */
export function ChatComposerContextBar({
  item,
  onRemove,
}: ChatComposerContextBarProps) {
  const Icon = ICON_BY_CONTEXT_TYPE[item.type];

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        width: '100%',
        px: 1,
        py: 0.5,
        bgcolor: COLORS.GRAY[50],
        borderBottom: `1px solid ${COLORS.GRAY[200]}`,
        // Follow the composer input's rounded corners so the strip's
        // background doesn't poke past the outlined border at the top.
        borderTopLeftRadius: 'inherit',
        borderTopRightRadius: 'inherit',
      }}
    >
      {/* minWidth: 0 lets the chip shrink inside the space-between row so a
          long object name truncates instead of pushing out the close button */}
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{
          minWidth: 0,
          px: 0.5,
          py: 0.25,
        }}
      >
        <Icon
          size={16}
          aria-hidden
          sx={{ flexShrink: 0, color: COLORS.BLUE[500] }}
        />
        <Typography variant="body-xs" noWrap sx={{ fontWeight: 500 }}>
          {item.name}
        </Typography>
      </Stack>
      <IconButton
        icon={XCloseIcon}
        ariaLabel={`Remove ${item.name} from context`}
        variant="transparent"
        size="xs"
        // Compact the xs button's padding so the strip stays as short as the
        // design's 28px header.
        sx={{ p: 0.25, flexShrink: 0, color: COLORS.TEXT.SECONDARY }}
        onClick={onRemove}
      />
    </Stack>
  );
}
